import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@chakra-ui/react";
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Text,
  Badge,
  Button,
  HStack,
  VStack,
  Image,
  useColorModeValue,
  Icon,
  Skeleton,
  SkeletonCircle,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
} from "@chakra-ui/react";
import {
  AddIcon,
  CheckCircleIcon,
  CloseIcon,
  ExternalLinkIcon,
  WarningTwoIcon,
} from "@chakra-ui/icons";
import { FaCheck } from "react-icons/fa6";

import { DeliveryCompany } from "../../../entity/DeliveryCompany";
import { WSDeliveryInstance } from "../../../services/remote/delivery/WSDelivery";
import { ConnectDeliveryCompany } from "./components/ConnectDeliveryCompany";
import { StoreDeliveryConfigurations } from "../../../entity/StoreDeliveryConfiguration";
import { useSelector } from "react-redux";
import { selectStore } from "../../../store/selectors/StoreSelector";

export const StoreDeliveryScreen = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [selectedCompany, setSelectedCompany] =
    useState<DeliveryCompany | null>(null);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveryCompanies, setDeliveryCompanies] = useState<DeliveryCompany[]>(
    [],
  );
  const [storeDeliveryConfigurations, setStoreDeliveryConfigurations] =
    useState<StoreDeliveryConfigurations[]>([]);

  const store = useSelector(selectStore);

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const subtleBg = useColorModeValue("gray.50", "gray.700");
  const headingColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const statBg = useColorModeValue("white", "gray.800");

  const fetchDeliveryCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await WSDeliveryInstance.getDeliveryCompanies();
      setDeliveryCompanies(response);
    } catch (error) {
      console.error("Error fetching delivery companies:", error);
      setError(error as string);
      toast({
        title: t("delivery.FailedToFetch"),
        description: "Failed to load delivery companies. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreDeliveryConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response =
        await WSDeliveryInstance.getStoreDeliveryConfigurationsByStoreID(
          store?.id!,
        );
      setStoreDeliveryConfigurations(response || []); // Ensure it's always an array
    } catch (error) {
      console.error("Error fetching store delivery configurations:", error);
      setStoreDeliveryConfigurations([]); // Reset to empty array on error
      setError(error as string);
      toast({
        title: t("delivery.FailedToFetch"),
        description:
          "Failed to load store delivery configurations. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (company: DeliveryCompany) => {
    setSelectedCompany(company);
    setIsConnectModalOpen(true);
  };

  useEffect(() => {
    fetchDeliveryCompanies();
    fetchStoreDeliveryConfigurations();
  }, []);

  const stats = useMemo(() => {
    const total = deliveryCompanies.length;
    const active = deliveryCompanies.filter((c) => c.isActive).length;
    const connected = deliveryCompanies.filter((c) =>
      storeDeliveryConfigurations.some((cfg) => cfg.companyId === c.id),
    ).length;
    return { total, active, connected };
  }, [deliveryCompanies, storeDeliveryConfigurations]);

  const isConnected = (company: DeliveryCompany) =>
    storeDeliveryConfigurations.some(
      (config) => config.companyId === company.id,
    );

  if (loading) {
    return (
      <Box
        p={{ base: 4, md: 6 }}
        maxW="1400px"
        mx="auto"
        bg={pageBg}
        minH="100%"
      >
        <Skeleton height="28px" width="240px" mb={2} borderRadius="md" />
        <Skeleton height="16px" width="320px" mb={6} borderRadius="md" />
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4} mb={8}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height="80px" borderRadius="lg" />
          ))}
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Card
              key={i}
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
            >
              <CardHeader pb={3}>
                <HStack spacing={4}>
                  <SkeletonCircle size="14" />
                  <VStack align="start" flex={1} spacing={2}>
                    <Skeleton height="14px" width="80%" />
                    <Skeleton height="10px" width="60%" />
                  </VStack>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Skeleton height="36px" borderRadius="md" />
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={{ base: 4, md: 8 }} textAlign="center">
        <Icon as={WarningTwoIcon} boxSize={10} color="red.400" mb={3} />
        <Text color="red.500" fontSize="lg" fontWeight="medium">
          {t("delivery.FailedToFetch")}
        </Text>
        <Button
          mt={4}
          onClick={() => {
            fetchDeliveryCompanies();
            fetchStoreDeliveryConfigurations();
          }}
          colorScheme="blue"
        >
          {t("common.tryAgain", "Try Again")}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1400px" mx="auto">
      {/* Header */}
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        spacing={4}
        mb={6}
      >
        <Box>
          <Heading size={{ base: "md", md: "lg" }} color={headingColor}>
            {t("delivery.manageDeliveryCompanies")}
          </Heading>
          <Text color={mutedColor} mt={1} fontSize={{ base: "sm", md: "md" }}>
            {t("delivery.ConnectWithProviders")}
          </Text>
        </Box>
      </Stack>

      {/* Stats row */}
      {deliveryCompanies.length > 0 && (
        <SimpleGrid columns={{ base: 3 }} spacing={{ base: 2, md: 4 }} mb={8}>
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            px={{ base: 3, md: 5 }}
            py={{ base: 2, md: 3 }}
          >
            <StatLabel fontSize={{ base: "xs", md: "sm" }} color={mutedColor}>
              {t("delivery.total", "Total")}
            </StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "2xl" }}>
              {stats.total}
            </StatNumber>
          </Stat>
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            px={{ base: 3, md: 5 }}
            py={{ base: 2, md: 3 }}
          >
            <StatLabel fontSize={{ base: "xs", md: "sm" }} color={mutedColor}>
              {t("delivery.active", "Active")}
            </StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "2xl" }} color="green.500">
              {stats.active}
            </StatNumber>
          </Stat>
          <Stat
            bg={statBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            px={{ base: 3, md: 5 }}
            py={{ base: 2, md: 3 }}
          >
            <StatLabel fontSize={{ base: "xs", md: "sm" }} color={mutedColor}>
              {t("delivery.isConnected", "Connected")}
            </StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "2xl" }} color="blue.500">
              {stats.connected}
            </StatNumber>
          </Stat>
        </SimpleGrid>
      )}

      {/* Delivery Companies Grid */}
      {deliveryCompanies.length === 0 ? (
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody textAlign="center" py={12} px={4}>
            <Text fontSize="lg" color={mutedColor} mb={2}>
              {t("delivery.NoDeliveryCompaniesAvailable")}
            </Text>
            <Text color={mutedColor} fontSize="sm">
              {t("delivery.NoCompaniesMessage")}
            </Text>
          </CardBody>
        </Card>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={5}>
          {deliveryCompanies.map((company) => {
            const connected = isConnected(company);
            return (
              <Card
                key={company.id}
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                borderRadius="xl"
                overflow="hidden"
                _hover={{
                  shadow: "lg",
                  transform: { base: "none", md: "translateY(-2px)" },
                  borderColor: "gray.300",
                }}
                transition="all 0.2s"
                position="relative"
                display="flex"
                flexDirection="column"
              >
                <CardHeader pb={3}>
                  <HStack justify="space-between" align="start" spacing={3}>
                    {/* Company Logo and Info */}
                    <HStack spacing={3} flex={1} minW={0}>
                      {company.logo ? (
                        <Image
                          src={company.logo}
                          alt={company.name}
                          boxSize={{ base: "48px", md: "56px" }}
                          objectFit="contain"
                          borderRadius="md"
                          bg={subtleBg}
                          p={1}
                          flexShrink={0}
                          fallbackSrc="https://via.placeholder.com/60x60?text=Logo"
                        />
                      ) : (
                        <Box
                          boxSize={{ base: "48px", md: "56px" }}
                          bg={subtleBg}
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Icon as={AddIcon} boxSize={5} color="gray.400" />
                        </Box>
                      )}

                      <VStack align="start" spacing={1} flex={1} minW={0}>
                        <Heading size="sm" noOfLines={1}>
                          {company.name}
                        </Heading>
                        <Text
                          fontSize="xs"
                          color={mutedColor}
                          noOfLines={1}
                          wordBreak="break-all"
                        >
                          {company.baseUrl}
                        </Text>
                      </VStack>
                    </HStack>

                    {/* Status Badge */}
                    <Badge
                      colorScheme={company.isActive ? "green" : "red"}
                      variant="subtle"
                      fontSize="2xs"
                      px={2}
                      py={1}
                      borderRadius="full"
                      flexShrink={0}
                    >
                      <HStack spacing={1}>
                        <Icon
                          as={company.isActive ? CheckCircleIcon : CloseIcon}
                          boxSize={2.5}
                        />
                        <Text>
                          {company.isActive
                            ? t("delivery.active")
                            : t("delivery.inactive")}
                        </Text>
                      </HStack>
                    </Badge>
                  </HStack>
                </CardHeader>

                <CardBody pt={0} display="flex" flexDirection="column" flex={1}>
                  <VStack spacing={3} align="stretch" flex={1}>
                    {/* Fragments Info */}
                    {company.fragments && company.fragments.length > 0 && (
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={mutedColor}
                          mb={1.5}
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          {t("delivery.configuration_fields", {
                            count: company.fragments.length,
                          })}
                        </Text>
                        <Box
                          bg={subtleBg}
                          p={2.5}
                          borderRadius="md"
                          maxH="90px"
                          overflowY="auto"
                        >
                          {company.fragments
                            .slice(0, 2)
                            .map((fragment, index) => (
                              <Text
                                key={index}
                                fontSize="xs"
                                color={mutedColor}
                                noOfLines={1}
                              >
                                • {fragment.label || fragment.key}
                              </Text>
                            ))}
                          {company.fragments.length > 2 && (
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              +{company.fragments.length - 2} more...
                            </Text>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Box flex={1} />

                    <Divider />

                    {/* Dates */}
                    <HStack
                      justify="space-between"
                      fontSize="2xs"
                      color="gray.400"
                    >
                      <Text noOfLines={1}>
                        {t("delivery.Created")}{" "}
                        {company.createdAt
                          ? new Date(company.createdAt).toLocaleDateString()
                          : "N/A"}
                      </Text>
                      <Text noOfLines={1}>
                        {t("delivery.Updated")}{" "}
                        {company.updatedAt
                          ? new Date(company.updatedAt).toLocaleDateString()
                          : "N/A"}
                      </Text>
                    </HStack>

                    <Button
                      size="sm"
                      w="full"
                      isDisabled={!company.isActive}
                      colorScheme={connected ? "green" : "gray"}
                      variant={connected ? "outline" : "solid"}
                      leftIcon={
                        !company.isActive ? undefined : connected ? (
                          <FaCheck />
                        ) : (
                          <ExternalLinkIcon />
                        )
                      }
                      onClick={() => handleConnect(company)}
                    >
                      {!company.isActive
                        ? t("delivery.Unavailable")
                        : connected
                          ? t("delivery.isConnected")
                          : t("delivery.Connect")}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
      <ConnectDeliveryCompany
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        company={selectedCompany}
        onSuccess={() => {
          fetchDeliveryCompanies();
          setSelectedCompany(null);
        }}
      />
    </Box>
  );
};
